/**
 * The bridge between the thread's composer and terminal surfaces outside its
 * subtree.
 *
 * "Add to chat" on a terminal selection needs the composer's imperative
 * handle, which only ChatView's bindings hold. The right panel is a sibling of
 * ChatView under the route, not its descendant, so no prop thread can carry
 * the handle there. The handle publishes itself here when it is set; a
 * terminal surface forwards selections through this module and they land in
 * the mounted composer.
 *
 * One handle is published at a time: the active thread's. A background
 * thread's drawer keeps its own prop-threaded handler, so nothing that worked
 * changes behavior.
 */
import type { TerminalContextSelection } from "~/lib/terminalContext";

interface ComposerTerminalContextHandle {
  readonly addTerminalContext: (selection: TerminalContextSelection) => void;
}

let publishedHandle: ComposerTerminalContextHandle | null = null;

/**
 * Publishes the mounted composer's terminal-context handle, or clears it on
 * unmount, so surfaces outside ChatView's subtree can forward selections to
 * the one composer that is active.
 */
export function publishComposerTerminalContextHandle(
  handle: ComposerTerminalContextHandle | null,
): void {
  publishedHandle = handle;
}

/**
 * Forwards a selection to the mounted composer. A no-op only when no composer
 * is mounted, which for the panel's Terminal tab means a route transition: the
 * tab renders beside a mounted composer in every real state.
 */
export function addTerminalContextToThreadComposer(selection: TerminalContextSelection): void {
  publishedHandle?.addTerminalContext(selection);
}
